// Bloc « Mesure 0 » : où en est l'état des lieux déclaré par le client, et le
// bouton qui ouvre l'assistant.
//
// Il s'affiche sur la fiche de l'ÉTUDE « mesure zéro » (plus sur le tableau de
// bord) : c'est le point de départ, contre lequel le score prouvé par les
// autres études sera comparé.
import { useCallback, useState } from 'react';
import { useProductContext } from '../api/queries';
import { ApiRequestError } from '../api/client';
import { Button, Eyebrow } from '../components/ui';
import { Drawer } from '../write/Drawer';
import { useCanWrite } from '../write/useCanWrite';
import { Mesure0Wizard, REQUIRED_STEPS } from './Mesure0Wizard';
import './mesure0.css';

export function Mesure0Block({ projectId, projectName }: { projectId: string; projectName: string }) {
  const context = useProductContext(projectId);
  const canWrite = useCanWrite(projectId);
  const [open, setOpen] = useState(false);
  // Identité STABLE : le panneau relance sa gestion du focus à chaque changement
  // de `onClose`, ce qui volerait le curseur à chaque enregistrement.
  const close = useCallback(() => setOpen(false), []);

  if (context.isPending) return null;
  if (context.isError) {
    // Le tableau de bord s'est chargé, donc le projet EST dans le périmètre : un
    // 404 ici ne peut venir que d'un serveur qui ne connaît pas encore la route
    // (frontend recompilé, serveur pas relancé). On le dit, plutôt qu'un vague
    // « indisponible » qui laisse chercher un bouton qui n'apparaîtra pas.
    const stale = context.error instanceof ApiRequestError && context.error.status === 404;
    return (
      <section className="zy-card m0-block" role="status">
        <Eyebrow>Mesure 0</Eyebrow>
        <p className="zy-texte-muet">
          {stale
            ? 'Le serveur n’est pas à jour : redémarrez-le pour activer la mesure zéro.'
            : 'Le contexte produit est momentanément indisponible.'}
        </p>
        <Button variant="ghost" onClick={() => void context.refetch()}>
          Réessayer
        </Button>
      </section>
    );
  }

  const c = context.data;
  const started = c.completed > 0;
  const complete = c.completed === c.total;
  const label = !started ? 'Commencer la mesure' : complete ? 'Modifier la mesure' : 'Reprendre la mesure';
  const responses = c.questionnaire.clientResponses + (c.questionnaire.internalAnswered > 0 ? 1 : 0);

  return (
    <section className="zy-card m0-block" aria-labelledby="m0-title">
      <div className="m0-head">
        <div>
          <Eyebrow>Mesure 0</Eyebrow>
          <h2 className="t-h3" id="m0-title">
            {complete ? 'Contexte produit défini' : 'Ce que le client pense de son produit'}
          </h2>
          <p className="card-lede">
            {started
              ? 'La référence déclarée par le client, que vos études viendront confronter.'
              : 'Posez le point de départ avec le client : l’état des lieux du produit, ses fonctionnalités, ses utilisateurs, ce qui fonctionne ou non, et les piliers d’adoption. Vos études le confronteront ensuite à la réalité.'}
          </p>
        </div>
        {canWrite ? (
          <Button variant={started && complete ? 'ghost' : 'primary'} onClick={() => setOpen(true)}>
            {label}
          </Button>
        ) : null}
      </div>

      <ol className="m0-progress" aria-label={`${c.completed} étapes sur ${c.total} franchies`}>
        {REQUIRED_STEPS.map((s, i) => {
          const done = c.steps[s.key];
          return (
            <li key={s.key} className={`m0-chip${done ? ' m0-chip-done' : ''}`}>
              <span className="m0-chip-mark" aria-hidden="true">{done ? '✓' : i + 1}</span>
              {s.label}
              <span className="sr-only">{done ? ' — terminé' : ' — à faire'}</span>
            </li>
          );
        })}
      </ol>

      {started ? (
        <dl className="m0-summary">
          <div>
            <dt>Version</dt>
            <dd>{c.product.version ?? '—'}</dd>
          </div>
          <div>
            <dt>Fonctionnalités</dt>
            <dd>{c.features.length}</dd>
          </div>
          <div>
            <dt>Métiers</dt>
            <dd>{c.roles.length}</dd>
          </div>
          <div>
            <dt>Piliers notés</dt>
            <dd>
              {c.declaredScored}
              <span className="m0-of">/{c.pillars.length}</span>
            </dd>
          </div>
          <div>
            <dt>Questionnaires</dt>
            <dd>{responses}</dd>
          </div>
        </dl>
      ) : null}

      <Drawer
        open={open}
        title={`Mesure 0 · ${projectName}`}
        description="Ce que le client déclare de son produit : la référence de départ, avant toute étude."
        onClose={close}
      >
        {/* Rien de saisi encore : l'assistant commence par expliquer la démarche. */}
        <Mesure0Wizard projectId={projectId} projectName={projectName} context={c} welcome={!started} onClose={close} />
      </Drawer>
    </section>
  );
}
